import { useContext, useState } from 'react'

import trunk from '@/configureStore'
import { startImpersonation, stopImpersonation } from '@/services/impersonation.service'
import { RootStoreContext } from '@/stores/RootStore'
import { UserStoreContext } from '@/stores/UserStore'
import { getErrorMessage, toast } from '@/utils/helpers'
import { armIdentitySplash, clearIdentitySplash } from '@/utils/identitySplash'
import { HOME_PATH } from '@/utils/landing'

// Where a session *starts*, the same as a real login. Every authenticated user can open
// it (it falls through to the Dealers list for anyone without RO Intake), so an
// impersonated session never begins on a page its identity is not allowed to see. Also
// the fallback for the way back out.
const LANDING_PATH = HOME_PATH

// WARP-939 — where the actor was when they started, so exiting returns them to it.
//
// Exiting restores the actor's own identity, so unlike starting, the page they came from
// is by definition one they may open. Dropping them on the dashboard instead costs them
// the list they were working through — and with the Users tab that list is now the whole
// point of the trip.
//
// sessionStorage, because the swap reloads the page and this belongs to the one tab.
// Every access is guarded: storage throws outright in private mode, and losing the
// return path must never break the exit it accompanies.
const RETURN_PATH_KEY = 'warp:impersonation-return-path'

const rememberReturnPath = () => {
  try {
    window.sessionStorage.setItem(
      RETURN_PATH_KEY,
      window.location.pathname + window.location.search,
    )
  } catch {
    // Storage unavailable — exiting falls back to the landing path.
  }
}

const takeReturnPath = () => {
  let stored = null

  try {
    stored = window.sessionStorage.getItem(RETURN_PATH_KEY)
    window.sessionStorage.removeItem(RETURN_PATH_KEY)
  } catch {
    return null
  }

  // A relative, same-origin path only. The value is read back out of storage, so a
  // tampered one must not be able to turn the exit into a redirect off the site.
  if (!stored || !stored.startsWith('/') || stored.startsWith('//')) return null

  return stored
}

/**
 * WARP-567 — starts and ends a read-only impersonation ("View as") session.
 *
 * Swapping identity reloads the page on purpose: it drops all in-memory component state
 * and re-runs GlobalSettingsStore's load reaction, which keys on *becoming*
 * authenticated and so would not re-fire when only the identity changes.
 *
 * The reload alone is not enough, though. AsyncTrunk persists the whole RootStore, so
 * the dashboard, dealer and billing filters, the notification list and the task state
 * are written to localStorage and come straight back on the next boot still belonging to
 * whoever was signed in. They are reset explicitly before persisting, which is the
 * difference between showing the right data and showing one user's data under another's
 * name.
 */
export const useImpersonation = () => {
  const { setImpersonation, clearImpersonation } = useContext(UserStoreContext)
  const { resetIdentityScopedStores } = useContext(RootStoreContext)
  const [loading, setLoading] = useState(false)

  /**
   * @param resolveDestination - Where to land, resolved only once the swap has
   *   succeeded. Deferred on purpose: reading the return path consumes it, and a failed
   *   exit leaves the caller still impersonating and able to try again.
   */
  const swapIdentity = async (
    request,
    applyToStore,
    splash,
    resolveDestination = () => LANDING_PATH,
  ) => {
    setLoading(true)
    // Up front, so the wait for the request and the reload that follows read as one
    // continuous action rather than a frozen button and then a blank page.
    armIdentitySplash(splash.message, splash.detail)

    try {
      const data = await request()
      applyToStore(data)
      // After applying the new identity, so the tokens and user just set survive, and
      // before persisting, so the previous identity's caches are never written out.
      resetIdentityScopedStores()
      await trunk.persist()
      window.location.assign(resolveDestination())
    } catch (err) {
      // No reload is coming, so take the splash back down.
      clearIdentitySplash()
      toast(getErrorMessage(err), 'error')
      setLoading(false)
    }
  }

  /**
   * Start viewing as `userId`.
   * @param {string} userId
   * @param {string} name - Who is being viewed, for the splash. Falls back to a generic
   *   phrase, so a missing name never leaves the screen wordless.
   */
  const viewAs = (userId, name) => {
    // Recorded before the swap, while the actor's own page is still on screen.
    rememberReturnPath()

    return swapIdentity(() => startImpersonation(userId), setImpersonation, {
      message: name ? `Switching to ${name}` : 'Switching accounts',
      detail: 'Setting up a read-only session',
    })
  }

  /** Return to your own account, and to the page you started from. */
  const exitViewAs = () =>
    swapIdentity(
      stopImpersonation,
      clearImpersonation,
      {
        message: 'Returning to your account',
        detail: 'Ending the read-only session',
      },
      () => takeReturnPath() || LANDING_PATH,
    )

  return { viewAs, exitViewAs, loading }
}

export default useImpersonation
